// API Service Layer for Smart Storage Application
import { fetchAuthSession } from 'aws-amplify/auth';
import { API_CONFIG } from '../aws-config';
import {
  CreateBucketRequest,
  CreateBucketResponse,
  UploadObjectRequest,
  UploadObjectResponse,
  DownloadObjectResponse,
  DeleteObjectResponse,
  RestoreObjectResponse,
  ListObjectVersionsResponse,
  CreateOtpShareResponse,
  ListOtpSharesResponse,
} from '../types';

/**
 * Parse limit information from 403 error message
 * Expected format: "Your {tier} tier allows {limit} {resource}. You currently have {current} {resource}. Upgrade to {nextTier} for {nextLimit} {resource}."
 */
export interface LimitInfo {
  limitType: 'crates' | 'storage' | 'shares';
  currentUsage: number;
  limit: number;
  currentTier: 'Free' | 'Pro' | 'Enterprise';
}

export function parseLimitError(errorMessage: string): LimitInfo | null {
  if (!errorMessage) return null;

  // Extract tier
  const tierMatch = errorMessage.match(/Your (Free|Pro|Enterprise) tier/i);
  if (!tierMatch) return null;
  const currentTier = tierMatch[1] as 'Free' | 'Pro' | 'Enterprise';

  // Determine limit type
  let limitType: 'crates' | 'storage' | 'shares';
  if (errorMessage.includes('share')) {
    limitType = 'shares';
  } else if (errorMessage.includes('storage') || errorMessage.includes('GB')) {
    limitType = 'storage';
  } else if (errorMessage.includes('crate')) {
    limitType = 'crates';
  } else {
    return null;
  }

  // Extract current usage and limit
  // Pattern: "allows X crates. You currently have Y crates"
  // Pattern: "allows X.XX GB storage. You currently have Y.YY GB used"
  const allowsMatch = errorMessage.match(/allows (\d+(?:\.\d+)?)\s*(?:GB)?/i);
  const currentMatch = errorMessage.match(/currently have (\d+(?:\.\d+)?)\s*(?:GB)?/i);

  if (!allowsMatch || !currentMatch) return null;

  const limit = parseFloat(allowsMatch[1]);
  const currentUsage = parseFloat(currentMatch[1]);

  // Convert GB to bytes for storage
  if (limitType === 'storage') {
    return {
      limitType,
      currentUsage: currentUsage * 1024 * 1024 * 1024, // Convert GB to bytes
      limit: limit * 1024 * 1024 * 1024, // Convert GB to bytes
      currentTier
    };
  }

  return {
    limitType,
    currentUsage,
    limit,
    currentTier
  };
}

class ApiService {
  private baseURL: string;

  constructor() {
    this.baseURL = API_CONFIG.baseURL;
  }

  private async getAuthHeaders(forceRefresh: boolean = false): Promise<HeadersInit> {
    try {
      const session = await fetchAuthSession({ forceRefresh });
      const token = session.tokens?.idToken?.toString();
      
      if (!token) {
        throw new Error('No authentication token available');
      }
      
      return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      };
    } catch (error) {
      console.error('Failed to get auth headers:', error);
      throw new Error('Authentication required. Please log in again.');
    }
  }

  private async fetchWithAuth(url: string, options: RequestInit, retryOnAuth: boolean = true): Promise<Response> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(url, {
      ...options,
      headers: { ...headers, ...options.headers },
    });

    // If we get 401/403 and haven't retried yet, refresh token and retry
    if ((response.status === 401 || response.status === 403) && retryOnAuth) {
      console.log('Auth error, refreshing token and retrying...');
      const refreshedHeaders = await this.getAuthHeaders(true);
      return fetch(url, {
        ...options,
        headers: { ...refreshedHeaders, ...options.headers },
      });
    }

    return response;
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
      
      // For 403 errors, include status code and full error data for limit detection
      if (response.status === 403) {
        const error: any = new Error(errorData.error || errorData.message || 'Forbidden');
        error.statusCode = 403;
        error.errorData = errorData;
        throw error;
      }
      
      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }
    return response.json();
  }

  async createBucket(request: CreateBucketRequest): Promise<CreateBucketResponse> {
    const headers = await this.getAuthHeaders();
    const body: Record<string, string> = {
      bucketName: request.bucketName,
      provider: request.provider || 'S3',
    };
    if (request.regionOverride) body.regionOverride = request.regionOverride;
    const response = await fetch(`${this.baseURL}/buckets`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
    return this.handleResponse<CreateBucketResponse>(response);
  }

  async listBuckets(): Promise<{ success: boolean; buckets: Array<{ name: string; creationDate: string; region: string }>; message: string }> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.baseURL}/buckets`, {
      method: 'GET',
      headers,
    });
    return this.handleResponse(response);
  }

  async deleteBucket(bucketName: string): Promise<{ success: boolean; message: string }> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.baseURL}/buckets/${bucketName}`, {
      method: 'DELETE',
      headers,
    });
    return this.handleResponse(response);
  }

  async emptyBucket(bucketName: string): Promise<{ success: boolean; message: string; deletedCount: number }> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.baseURL}/buckets/${bucketName}/empty`, {
      method: 'POST',
      headers,
    });
    return this.handleResponse(response);
  }

  async uploadObject(request: UploadObjectRequest): Promise<UploadObjectResponse> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(
      `${this.baseURL}/buckets/${request.bucketName}/objects`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({
          objectKey: request.objectKey,
          fileContent: request.fileContent,
          contentType: request.contentType,
        }),
      }
    );
    return this.handleResponse<UploadObjectResponse>(response);
  }

  async generatePresignedUpload(bucketName: string, objectKey: string, contentType: string, fileSize: number): Promise<any> {
    const response = await this.fetchWithAuth(
      `${this.baseURL}/buckets/${bucketName}/presigned-upload`,
      {
        method: 'POST',
        body: JSON.stringify({
          objectKey,
          contentType,
          fileSize
        }),
      }
    );
    return this.handleResponse(response);
  }

  async completeMultipartUpload(bucketName: string, objectKey: string, uploadId: string, parts: Array<{PartNumber: number, ETag: string}>): Promise<any> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(
      `${this.baseURL}/buckets/${bucketName}/complete-multipart-upload`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({
          objectKey,
          uploadId,
          parts
        }),
      }
    );
    return this.handleResponse(response);
  }

  async abortMultipartUpload(bucketName: string, objectKey: string, uploadId: string): Promise<any> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(
      `${this.baseURL}/buckets/${bucketName}/abort-multipart-upload`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({
          objectKey,
          uploadId
        }),
      }
    );
    return this.handleResponse(response);
  }

  async downloadObject(bucketName: string, objectKey: string): Promise<DownloadObjectResponse> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(
      `${this.baseURL}/buckets/${bucketName}/objects/${encodeURIComponent(objectKey)}`,
      {
        method: 'GET',
        headers,
      }
    );
    return this.handleResponse<DownloadObjectResponse>(response);
  }

  async deleteObject(bucketName: string, objectKey: string): Promise<DeleteObjectResponse> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(
      `${this.baseURL}/buckets/${bucketName}/objects/${encodeURIComponent(objectKey)}`,
      {
        method: 'DELETE',
        headers,
      }
    );
    return this.handleResponse<DeleteObjectResponse>(response);
  }

  async permanentlyDeleteObject(bucketName: string, objectKey: string): Promise<{
    success: boolean;
    objectKey: string;
    deletedVersions: number;
    message: string;
  }> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(
      `${this.baseURL}/buckets/${bucketName}/objects/${encodeURIComponent(objectKey)}/permanent-delete`,
      {
        method: 'POST',
        headers,
      }
    );
    return this.handleResponse(response);
  }

  async copyObjects(
    sourceBucket: string,
    destBucket: string,
    objectKeys: string[],
    operation: 'copy' | 'move',
    onProgress?: (done: number, total: number) => void
  ): Promise<{ success: boolean; copiedCount: number; failedCount: number; message: string }> {
    const headers = await this.getAuthHeaders();

    // Step 1: initiate — Lambda returns server-side result OR presigned URLs
    const initRes = await fetch(`${this.baseURL}/objects/copy`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ sourceBucket, destBucket, objectKeys, operation })
    });
    const initData = await this.handleResponse<any>(initRes);

    if (initData.mode === 'server-side') {
      return {
        success: true,
        copiedCount: initData.copiedCount ?? 0,
        failedCount: initData.failedCount ?? 0,
        message: `${operation === 'move' ? 'Moved' : 'Copied'} ${initData.copiedCount} file(s)`
      };
    }

    // Step 2: client-transfer — browser streams each file directly source → dest
    const transfers: Array<{ key: string; downloadUrl: string; uploadUrl: string; contentType: string }> =
      initData.transfers ?? [];
    const completedKeys: string[] = [];
    let failedCount = (initData.failedKeys ?? []).length;
    const total = transfers.length;

    for (let i = 0; i < transfers.length; i++) {
      const t = transfers[i];
      try {
        // Download the file as a blob then PUT it — no Lambda in data path
        const dlRes = await fetch(t.downloadUrl);
        if (!dlRes.ok) throw new Error(`Download HTTP ${dlRes.status}`);
        const blob = await dlRes.blob();
        const putRes = await fetch(t.uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': t.contentType },
          body: blob
        });
        if (!putRes.ok) throw new Error(`Upload HTTP ${putRes.status}`);
        completedKeys.push(t.key);
      } catch (e) {
        console.error(`Transfer failed for ${t.key}:`, e);
        failedCount++;
      }
      onProgress?.(i + 1, total);
    }

    // Step 3: complete — Lambda updates storage accounting + handles move soft-deletes
    if (completedKeys.length > 0) {
      const completeHeaders = await this.getAuthHeaders();
      await fetch(`${this.baseURL}/objects/copy/complete`, {
        method: 'POST',
        headers: completeHeaders,
        body: JSON.stringify({ sourceBucket, destBucket, completedKeys, operation })
      });
    }

    return {
      success: true,
      copiedCount: completedKeys.length,
      failedCount,
      message: `${operation === 'move' ? 'Moved' : 'Copied'} ${completedKeys.length} file(s)`
    };
  }

  async restoreObject(bucketName: string, objectKey: string): Promise<RestoreObjectResponse> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(
      `${this.baseURL}/buckets/${bucketName}/objects/${encodeURIComponent(objectKey)}/restore`,
      {
        method: 'POST',
        headers,
      }
    );
    return this.handleResponse<RestoreObjectResponse>(response);
  }

  async listObjects(bucketName: string): Promise<Array<{ key: string; size: number; lastModified: string }>> {
    const response = await this.listObjectVersions(bucketName);
    
    // Get only the latest non-deleted versions
    const latestVersions = new Map<string, any>();
    
    if (response.versions && response.versions.length > 0) {
      response.versions.forEach((version) => {
        const normalizedKey = version.key.trim();
        const existing = latestVersions.get(normalizedKey);
        
        if (!existing || version.isLatest) {
          // Skip delete markers
          if (!version.isDeleteMarker) {
            latestVersions.set(normalizedKey, version);
          }
        }
      });
    }
    
    return Array.from(latestVersions.values()).map(v => ({
      key: v.key,
      size: v.size || 0,
      lastModified: v.lastModified
    }));
  }

  async listObjectVersions(
    bucketName: string,
    objectKey?: string
  ): Promise<ListObjectVersionsResponse> {
    const headers = await this.getAuthHeaders();
    const url = objectKey
      ? `${this.baseURL}/buckets/${bucketName}/objects/${encodeURIComponent(objectKey)}/versions`
      : `${this.baseURL}/buckets/${bucketName}/versions`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });
    return this.handleResponse<ListObjectVersionsResponse>(response);
  }

  async deleteAccount(): Promise<{ 
    success: boolean; 
    message: string;
    username?: string;
    lyveUserDeleted?: boolean;
    lyveMessage?: string;
    totalBuckets?: number;
  }> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.baseURL}/account`, {
      method: 'DELETE',
      headers,
    });
    return this.handleResponse<{ 
      success: boolean; 
      message: string;
      username?: string;
      lyveUserDeleted?: boolean;
      lyveMessage?: string;
      totalBuckets?: number;
    }>(response);
  }

  async checkHealth(): Promise<{ status: string; timestamp: string }> {
    const response = await fetch(`${this.baseURL}/health`, {
      method: 'GET',
    });
    return this.handleResponse<{ status: string; timestamp: string }>(response);
  }

  // Share-related methods
  async createShare(bucketName: string, objectKey: string, expirationDays: number, recipientEmail?: string): Promise<{
    success: boolean;
    shareToken?: string;
    shareUrl?: string;
    expiresAt?: string;
    message: string;
  }> {
    const headers = await this.getAuthHeaders();
    const body: Record<string, unknown> = { bucketName, objectKey, expirationDays };
    if (recipientEmail) body.recipientEmail = recipientEmail;
    const response = await fetch(`${this.baseURL}/shares`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
    return this.handleResponse(response);
  }

  async listShares(): Promise<{
    success: boolean;
    shares: Array<{
      shareToken: string;
      bucketName: string;
      objectKey: string;
      shareUrl: string;
      createdAt: string;
      expiresAt: string;
    }>;
  }> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.baseURL}/shares`, {
      method: 'GET',
      headers,
    });
    return this.handleResponse(response);
  }

  async revokeShare(shareToken: string): Promise<{
    success: boolean;
    message: string;
  }> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.baseURL}/shares/${shareToken}`, {
      method: 'DELETE',
      headers,
    });
    return this.handleResponse(response);
  }

  // Subscription-related methods
  async initializeSubscription(subscriptionId: string, planId: string): Promise<{
    success: boolean;
    message: string;
    tier?: string;
    status?: string;
  }> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.baseURL}/subscriptions`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        subscriptionId,
        planId
      }),
    });
    return this.handleResponse(response);
  }

  async getUsage(): Promise<{
    success: boolean;
    usage: {
      cratesCount: number;
      totalStorageBytes: number;
      sharesCount: number;
    };
  }> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.baseURL}/usage`, {
      method: 'GET',
      headers,
    });
    return this.handleResponse(response);
  }

  async getSubscription(): Promise<{
    subscription: {
      userId: string;
      tier: string;
      status: string;
      paypalSubscriptionId?: string;
      startDate: string;
      endDate?: string;
      nextBillingDate?: string;
      lastPaymentDate?: string;
    };
    usage: {
      cratesUsed: number;
      cratesLimit: number;
      storageUsed: number;
      storageLimit: number;
      sharesUsed: number;
      sharesLimit: number;
      secretsUsed: number;
      secretsLimit: number;
    };
  }> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.baseURL}/subscriptions`, {
      method: 'GET',
      headers,
    });
    const data = await this.handleResponse<any>(response);
    
    // Backend returns subscription fields at root level, but frontend expects them nested
    // Transform the response to match frontend expectations
    return {
      subscription: {
        userId: data.userId,
        tier: data.tier,
        status: data.status,
        startDate: data.startDate,
        paypalSubscriptionId: data.paypalSubscriptionId,
        nextBillingDate: data.nextBillingDate,
        endDate: data.endDate,
        lastPaymentDate: data.lastPaymentDate
      },
      usage: data.usage
    };
  }

  async cancelSubscription(): Promise<{
    success: boolean;
    message: string;
    effectiveDate?: string;
  }> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.baseURL}/subscriptions/cancel`, {
      method: 'POST',
      headers,
    });
    return this.handleResponse(response);
  }

  // OTP Share methods
  async createOtpShare(secret: string, recipientEmail: string, expirationDays: number): Promise<CreateOtpShareResponse> {
    const response = await this.fetchWithAuth(`${this.baseURL}/otp-shares`, {
      method: 'POST',
      body: JSON.stringify({ secret, recipientEmail, expirationDays }),
    });
    return this.handleResponse<CreateOtpShareResponse>(response);
  }

  async listOtpShares(): Promise<ListOtpSharesResponse> {
    const response = await this.fetchWithAuth(`${this.baseURL}/otp-shares`, { method: 'GET' });
    return this.handleResponse<ListOtpSharesResponse>(response);
  }

  async deleteOtpShare(token: string): Promise<{ success: boolean; message: string }> {
    const response = await this.fetchWithAuth(`${this.baseURL}/otp-shares/${token}`, { method: 'DELETE' });
    return this.handleResponse(response);
  }

  async createFolder(bucketName: string, folderPath: string): Promise<void> {
    // Route through the backend Lambda (upload_object) so the request goes
    // browser → API Gateway → Lambda → S3/Lyve. This avoids browser CORS
    // issues with direct-to-Lyve presigned URL requests.
    await this.uploadObject({
      bucketName,
      objectKey: folderPath + '/',
      fileContent: '',  // empty base64 — Lambda accepts empty string for folders
      contentType: 'application/x-directory',
    });
  }

  async getActivity(limit: number = 5): Promise<{ activity: Array<{ timestamp: string; action: string; details: string }> }> {
    const response = await this.fetchWithAuth(`${this.baseURL}/activity?limit=${limit}`, { method: 'GET' });
    return this.handleResponse(response);
  }

  async adminGetUsers(): Promise<{
    users: Array<{
      userId: string;
      cognitoUsername: string;
      username: string;
      email: string;
      pool: string;
      poolId: string;
      tier: string;
      storageGB: number;
      shares: number;
      secrets: number;
      enabled: boolean;
    }>;
    stats: { total: number; free: number; paid: number };
  }> {
    const response = await this.fetchWithAuth(`${this.baseURL}/admin/users`, { method: 'GET' });
    return this.handleResponse(response);
  }

  async adminGetUserActivity(userId: string, limit: number = 10): Promise<{ activity: Array<{ timestamp: string; action: string; details: string }>; userId: string }> {
    const response = await this.fetchWithAuth(`${this.baseURL}/admin/users/${userId}/activity?limit=${limit}`, { method: 'GET' });
    return this.handleResponse(response);
  }

  async adminRemoveMfa(userId: string, pool: 'personal' | 'business'): Promise<{ success: boolean; message: string }> {
    const response = await this.fetchWithAuth(`${this.baseURL}/admin/users/${userId}/remove-mfa`, {
      method: 'POST',
      body: JSON.stringify({ pool }),
    });
    return this.handleResponse(response);
  }

  async sendPhoneOtp(phoneNumber: string): Promise<{ message: string }> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.baseURL}/phone/send-otp`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ phone_number: phoneNumber }),
    });
    return this.handleResponse(response);
  }

  async verifyPhoneOtp(code: string, phoneNumber: string): Promise<{ message: string }> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.baseURL}/phone/verify-otp`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ code, phone_number: phoneNumber }),
    });
    return this.handleResponse(response);
  }
}

export const apiService = new ApiService();
